import { DesignSystem } from '@everfi/design-system';
import { kebabCase } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { components } from 'react-select';
import { Chip } from '../../Chip/component';
import { SelectCheckboxOption } from './SelectCheckboxOption';
import { SelectClearTooltip } from './SelectClearTooltip';

const {
  DropdownIndicator,
  IndicatorsContainer,
  IndicatorSeparator,
  Input,
  MenuList,
  SelectContainer,
  SingleValue,
  ValueContainer,
  MenuPortal,
} = components;

const SelectComponentWrapper = ({
  Component = <></>,
  componentName = '',
  componentProps = {},
  wrapperAttributes = {},
  wrapperStyle = {},
}) => {
  const { selectProps } = componentProps;
  const formattedName = selectProps?.name?.replace?.('[]', '') ?? '';
  const name = formattedName ? `-${formattedName}` : '';
  const dataObjectId = `Select${name}-${componentName}`;

  return (
    <div {...wrapperAttributes} data-object-id={dataObjectId} style={wrapperStyle}>
      <Component {...componentProps} />
    </div>
  );
};

export const DropdownIndicatorWrapper = props => (
  <SelectComponentWrapper Component={DropdownIndicator} componentName="dropdown-indicator" componentProps={props} />
);

export const IndicatorsContainerWrapper = props => (
  <SelectComponentWrapper Component={IndicatorsContainer} componentName="indicators" componentProps={props} />
);

export const IndicatorsSeparatorWrapper = props => (
  <SelectComponentWrapper Component={IndicatorSeparator} componentName="indicator-separator" componentProps={props} />
);

export const MenuListWrapper = props => (
  <SelectComponentWrapper Component={MenuList} componentName="menu-list" componentProps={props} />
);

export const MenuPortalWrapper = props => {
  return <MenuPortal {...props} />;
};

export const MultiValueWrapper = (props = {}) => {
  const { children, removeProps } = props;
  const label = props?.data?.label || children;
  const wrapperAttributes = {
    'data-component-type': `${kebabCase(label)}-chip`,
  };
  const componentProps = {
    ...props,
    value: label,
    onRemove: removeProps?.onClick,
    css: { mr: DesignSystem.Space.XS, mb: DesignSystem.Space.XS },
  };

  return (
    <SelectComponentWrapper
      Component={Chip}
      componentName="multi-value"
      componentProps={componentProps}
      wrapperAttributes={wrapperAttributes}
    />
  );
};

export const SelectContainerWrapper = props => (
  <SelectComponentWrapper Component={SelectContainer} componentName="container" componentProps={props} />
);

export const SingleValueWrapper = (props = {}) => {
  const wrapperAttributes = {
    'data-component-type': `${kebabCase(props?.data?.label)}-value`,
  };

  return (
    <SelectComponentWrapper
      Component={SingleValue}
      componentName="single-value"
      componentProps={props}
      wrapperAttributes={wrapperAttributes}
      wrapperStyle={{ gridArea: '1/1/2/3' }}
    />
  );
};

export const ValueContainerWrapper = props => (
  <SelectComponentWrapper Component={ValueContainer} componentName="value-container" componentProps={props} />
);

/**
 * Custom Input wrapper that merges the external aria-describedby (passed via selectProps
 * from FormField) with react-select's internally generated aria-describedby. Without this,
 * react-select overrides our aria-describedby with its own, breaking the link between the
 * input and the error/hint message rendered by FormField.
 *
 * NOTE: This is exported separately and NOT included in the shared `selectComponents` object
 * because filter selects (SelectFilter) also import `selectComponents` and should not be affected.
 *
 * NOTE: This only works for **searchable** selects. When isSearchable is false, react-select
 * renders its own internal DummyInput and completely bypasses custom Input components.
 * For non-searchable selects, use `patchDummyInputAriaDescribedBy` after render instead.
 */
export const InputWrapper = props => {
  const externalDescribedBy = props.selectProps?.['aria-describedby'];
  const internalDescribedBy = props['aria-describedby'];
  const mergedDescribedBy = [internalDescribedBy, externalDescribedBy].filter(Boolean).join(' ') || undefined;

  return <Input {...props} aria-describedby={mergedDescribedBy} />;
};

InputWrapper.propTypes = {
  selectProps: PropTypes.object,
  'aria-describedby': PropTypes.string,
};

/**
 * Patches aria-describedby on react-select's internal DummyInput for non-searchable selects.
 *
 * When isSearchable is false, react-select renders its own DummyInput directly and bypasses
 * our custom InputWrapper component entirely. This helper finds the combobox input inside the
 * given container and merges the external aria-describedby (from FormField) with react-select's
 * internally generated value, so screen readers can announce error/hint messages.
 *
 * Call this from componentDidMount and componentDidUpdate in SelectControl/SelectAsyncControl.
 *
 * @param {HTMLElement|null} containerEl - The DOM container wrapping the react-select instance
 * @param {string|undefined} externalDescribedBy - The aria-describedby value from FormField (e.g. "name-hint")
 */
export const patchDummyInputAriaDescribedBy = (containerEl, externalDescribedBy) => {
  if (!containerEl || !externalDescribedBy) return;

  const input = containerEl.querySelector('input[role="combobox"]');
  if (!input) return;

  const currentDescribedBy = input.getAttribute('aria-describedby') || '';
  if (currentDescribedBy.includes(externalDescribedBy)) return;

  const merged = [currentDescribedBy, externalDescribedBy].filter(Boolean).join(' ');
  input.setAttribute('aria-describedby', merged);
};

const propTypes = {
  Component: PropTypes.func,
  componentName: PropTypes.string,
  componentProps: PropTypes.object,
  data: PropTypes.shape({
    label: PropTypes.string,
  }),
  wrapperAttributes: PropTypes.object,
  wrapperStyle: PropTypes.object,
};

SelectComponentWrapper.propTypes = propTypes;
DropdownIndicatorWrapper.propTypes = propTypes;
IndicatorsContainerWrapper.propTypes = propTypes;
IndicatorsSeparatorWrapper.propTypes = propTypes;
MenuListWrapper.propTypes = propTypes;
MultiValueWrapper.propTypes = propTypes;
SelectContainerWrapper.propTypes = propTypes;
SingleValueWrapper.propTypes = propTypes;
ValueContainerWrapper.propTypes = propTypes;
MenuPortalWrapper.propTypes = propTypes;

export const selectComponents = {
  DropdownIndicator: DropdownIndicatorWrapper,
  IndicatorsContainer: IndicatorsContainerWrapper,
  IndicatorSeparator: IndicatorsSeparatorWrapper,
  MenuList: MenuListWrapper,
  MultiValue: MultiValueWrapper,
  MenuPortal: MenuPortalWrapper,
  Option: SelectCheckboxOption,
  SelectContainer: SelectContainerWrapper,
  SingleValue: SingleValueWrapper,
  ValueContainer: ValueContainerWrapper,
  ClearIndicator: SelectClearTooltip,
};
